// The video composer's parts, built from the one draft (`useComposer`): its slots, the Actions
// toolbar and panel, a tool's form of its own, the element editor and the prompt's tags. The
// Studio's floating composer (`VideoComposer`) and the Pro Studio redesign's Video page (spec
// ui-perf/pro-studio-redesign, PR c2) lay the same parts out each their own way.
import { useClickOutside, useFocusReturn, useHotkeys } from "@mantine/hooks";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactElement, type ReactNode } from "react";

import {
  arrivingTalkingHeadActor,
  tookTalkingHeadActor,
} from "../../../platform/actors/talking-head-handoff";
import type { ImageRefs } from "../../../platform/media/ImageRefsSlot";
import { useComposer, type ComposerProps, type FrameSlot } from "../composer/use-composer";
import { ActionsButton } from "../tools/ActionsButton";
import { ActionsPanel } from "../tools/ActionsPanel";
import { actionOf, type ActionTool, type ToolFormType } from "../tools";
import type { ToolFormProps } from "../tools/form-parts";
import { TalkingHeadForm } from "../tools/talking-head/TalkingHeadForm";
import { BackgroundForm } from "../tools/BackgroundForm";
import { DubbingForm } from "../tools/DubbingForm";
import { LipSyncForm } from "../tools/LipSyncForm";
import { MotionForm } from "../tools/MotionForm";
import { ReframeForm, UpscaleForm } from "../tools/ToolForms";
import { ToolSlots } from "../tools/ToolSlots";
import { ElementsEditor } from "./ElementsEditor";
import { KlingVoicesPanel, useVoiceProfilePages } from "./KlingVoicesPanel";
import type { VoicesProps } from "./VoicesSlot";
import type { Frame } from "./FrameSlot";
import { FrameSlots } from "./FrameSlots";
import type { MediaRefs } from "./MediaRefsSlot";

/** Live's library dialog from a frame's "From library". */
const FRAME_DIALOG = { title: "Select Frame", subtitle: "Choose an image for this frame" };

/** Each tool's form of its own, by its job type (G7-tool-forms). */
const FORMS: Record<ToolFormType, (props: ToolFormProps) => ReactElement | null> = {
  "video.upscale": UpscaleForm,
  "video.reframe": ReframeForm,
  "video.background": BackgroundForm,
  "video.motion": MotionForm,
  "video.talking_head": TalkingHeadForm,
  "video.dub": DubbingForm,
  "video.lipsync": LipSyncForm,
};

const asIs = (element: ReactElement) => element;

/** The video composer's parts over its draft; each page lays them out. */
export function useVideoParts(props: ComposerProps) {
  const c = useComposer(props);
  // Live's Actions panel: the pill opens it over the composer; a click outside it and its
  // toolbar, Escape or its x closes it, and the focus goes back where it was.
  const [actionsOpen, setActionsOpen] = useState(false);
  const [toolbar, setToolbar] = useState<HTMLDivElement | null>(null);
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  useClickOutside(() => setActionsOpen(false), null, [toolbar, panel]);
  useFocusReturn({ opened: actionsOpen });
  // Live's "Create Element" over the composer: a new element, or the one at its place.
  const [editing, setEditing] = useState<number | "new" | null>(null);
  // AI Actors' "Create a Talking Head": the actor in the navigation's state opens Talking Head
  // with it, once (live's route preset), where a model makes talking heads; leaving Talking Head
  // lets it go, so Talking Head again starts empty.
  const location = useLocation();
  const [arrival, setArrival] = useState(() => {
    const actor = arrivingTalkingHeadActor(location);
    const talkingHead = actionOf("talkingHead");
    return talkingHead !== null && c.catalog.video_models.some(talkingHead.takes) ? actor : null;
  });
  const presetDone = useRef(false);
  useEffect(() => {
    if (arrival === null || presetDone.current) return;
    presetDone.current = true;
    tookTalkingHeadActor(location);
    c.chooseAction(actionOf("talkingHead"));
  }, [arrival, c, location]);
  const chooseAction = (next: ActionTool | null) => {
    if (next?.id !== "talkingHead") setArrival(null);
    c.chooseAction(next);
  };
  // Live's advanced settings: a side panel the footer's button opens; Escape closes it (ours).
  const [advancedOpen, setAdvancedOpen] = useState(false);
  useHotkeys([["Escape", () => setAdvancedOpen(false)]]);
  // Kling 4.0's Voice Settings (G2-kling-v4-voices, layout A): the side panel the Voices tile
  // opens, in the advanced settings' place; opening one closes the other.
  const [voicesOpen, setVoicesOpen] = useState(false);
  const takesVoices = c.voicesState !== "hidden";
  // Every page of the brand's voices, read once: the panel lists them, the slots name them.
  const profiles = useVoiceProfilePages(c.teamId, c.brandId, takesVoices);
  const voiceNames = new Map(
    (profiles.data?.pages ?? []).flatMap((page) => page.items.map((each) => [each.id, each.name])),
  );
  const voiceModel =
    c.catalog.text_models.find((each) => each.job_types.includes("voice.profile"))?.id ?? null;
  const voices: VoicesProps | undefined = takesVoices
    ? {
        voices: c.draft.voices.map((id) => ({ id, name: voiceNames.get(id) ?? "A voice" })),
        tagged: c.voiceTags,
        disabled: c.voicesState === "disabled",
        onOpen: () => {
          setAdvancedOpen(false);
          setVoicesOpen(true);
        },
        onRemove: c.toggleVoice,
      }
    : undefined;
  const voicesPanel =
    voicesOpen && takesVoices ? (
      <KlingVoicesPanel
        teamId={c.teamId}
        brandId={c.brandId}
        profiles={profiles}
        model={voiceModel}
        picked={c.draft.voices}
        room={c.voicesRoom}
        onToggle={c.toggleVoice}
        onClose={() => setVoicesOpen(false)}
      />
    ) : undefined;
  // The products' popover hangs from the slot it fills.
  const frame = (
    slot: FrameSlot,
    wrap: (element: ReactElement) => ReactElement,
  ): Frame => ({
    view: slot === "start" ? c.view : c.endView,
    onFromLibrary: () => c.openLibrary(FRAME_DIALOG, slot),
    onGenerate: (slot === "start" ? c.frameGenerate : c.endGenerate).open,
    onFromProduct: () => c.openProducts(slot),
    onUpload: (file) => void (slot === "start" ? c.frameUpload : c.endUpload).send(file),
    uploading: (slot === "start" ? c.frameUpload : c.endUpload).pending,
    onRemove: slot === "start" ? c.removeSource : c.removeEndFrame,
    wrap: c.productsSlot === slot ? wrap : asIs,
    disabled: slot === "end" ? c.endFrameBlocked : c.startState === "disabled",
    limits: c.limits.image,
  });
  // Live's Video ref and Audio refs (#260), where a mode of the model takes them.
  const media = (field: "videos" | "audios"): MediaRefs | undefined => {
    const state = field === "videos" ? c.videosState : c.audiosState;
    if (state === "hidden") return undefined;
    const upload = field === "videos" ? c.videoUpload : c.audioUpload;
    return {
      items: c.shownMedia(field),
      tagged: field === "videos" ? c.videoTags : c.audioTags,
      disabled: state === "disabled",
      full: c.draft[field].length >= (field === "videos" ? c.videosRoom : c.audiosRoom),
      limits: field === "videos" ? c.limits.video : c.limits.audio,
      onFromLibrary: () => c.openMedia(field),
      onUpload: (file) => void upload.send(file),
      uploading: upload.pending,
      onRemove: (assetId) => c.removeMedia(field, assetId),
    };
  };
  // Live's Image refs (#232), where a mode of the model takes them.
  const refs = (wrap: (element: ReactElement) => ReactElement): ImageRefs | undefined =>
    c.referencesState === "hidden"
      ? undefined
      : {
          images: c.references,
          tagged: c.imageTags,
          disabled: c.referencesState === "disabled",
          full: c.references.length >= c.referencesRoom,
          onFromLibrary: c.openReferences,
          onGenerate: c.referenceGenerate.open,
          onFromProduct: () => c.openProducts("references"),
          // One file at a time, as before #598 (live's Studio takes several: G6's note).
          onUpload: (files) => void c.referenceUpload.send(files[0] ?? null),
          uploading: c.referenceUpload.pending,
          onRemove: c.removeReference,
          wrap: c.productsSlot === "references" ? wrap : asIs,
          limits: c.limits.image,
        };
  // Kling 4.0's keyframes between the frames (ours, spec web/keyframes-slot): live's Image refs
  // tile, each keyframe's pill its place in the order.
  const keyframes = (wrap: (element: ReactElement) => ReactElement): ImageRefs | undefined =>
    c.keyframesState === "hidden"
      ? undefined
      : {
          holds: "keyframes",
          images: c.shownKeyframes,
          tagged: false,
          disabled: c.keyframesState === "disabled",
          full: c.draft.middleFrames.length >= c.keyframesRoom,
          onFromLibrary: c.openKeyframes,
          onGenerate: c.keyframeGenerate.open,
          onFromProduct: () => c.openProducts("keyframes"),
          onUpload: (files) => void c.keyframeUpload.send(files[0] ?? null),
          uploading: c.keyframeUpload.pending,
          onRemove: c.removeKeyframe,
          wrap: c.productsSlot === "keyframes" ? wrap : asIs,
          limits: c.limits.image,
        };
  const elements =
    c.elementsState === "hidden" || c.elementCaps === null
      ? undefined
      : {
          elements: c.shownElements,
          tagged: c.elementTags,
          disabled: c.elementsState === "disabled",
          full: c.draft.elements.length >= c.elementCaps.max,
          onCreate: () => setEditing("new"),
          onEdit: (at: number) => setEditing(at),
          onRemove: c.removeElement,
        };
  // A tool's reference videos follow its own video, which is `@Video1` whether chosen yet or not
  // (as FAL's Kling 4.0 Edit reads them): theirs are `@Video2` on, in their pills and the prompt's
  // tags alike, so a tag written before the tool's video is chosen keeps naming the same video.
  const toolVideos = c.tool ? media("videos") : undefined;
  const videoTagNames = c.tool
    ? [...c.sourceVideos.map(() => "Video1"), ...c.draft.videos.map((_, at) => `Video${at + 2}`)]
    : c.draft.videos.map((_, at) => `Video${at + 1}`);
  const header = c.tool ? (
    // A tool on the composer (Video Edit, #209): its own slots, the video it works on, then its
    // reference videos.
    c.products((wrap) => (
      <ToolSlots
        elements={elements}
        refs={refs(wrap)}
        videos={toolVideos === undefined ? undefined : { ...toolVideos, firstTag: 2 }}
        video={{
          items: c.sourceVideos,
          tagged: c.videoTags,
          disabled: false,
          full: c.sourceVideos.length > 0,
          limits: c.limits.video,
          onFromLibrary: c.openSourceVideo,
          onUpload: (file) => void c.sourceVideoUpload.send(file),
          uploading: c.sourceVideoUpload.pending,
          onRemove: c.removeSourceVideo,
        }}
      />
    ))
  ) : c.canSource ? (
    c.products((wrap) => (
      <FrameSlots
        start={frame("start", wrap)}
        end={c.endFrameOffered ? frame("end", wrap) : undefined}
        onSwap={c.swapFrames}
        keyframes={keyframes(wrap)}
        refs={refs(wrap)}
        videos={media("videos")}
        audios={media("audios")}
        elements={elements}
        voices={voices}
      />
    ))
  ) : (
    <FrameSlots start={undefined} end={undefined} onSwap={c.swapFrames} />
  );
  const above = (
    <div ref={setToolbar}>
      <ActionsButton
        open={actionsOpen}
        onToggle={() => setActionsOpen((open) => !open)}
        chosen={c.action}
        onClear={() => chooseAction(null)}
      />
    </div>
  );
  const overlay = actionsOpen ? (
    <div ref={setPanel}>
      <ActionsPanel
        catalog={c.catalog}
        chosen={c.action}
        onChoose={(action) => {
          chooseAction(action);
          setActionsOpen(false);
        }}
        onClose={() => setActionsOpen(false)}
      />
    </div>
  ) : undefined;
  // The prompt's tags: the images, the videos (a tool's first), the audios and the elements.
  const promptTags = [
    ...(c.imageTags ? c.references.map((_, at) => `Image${at + 1}`) : []),
    // The tool's video (Video Edit's), then the reference videos: one numbering.
    ...(c.videoTags ? videoTagNames : []),
    ...(c.audioTags ? c.draft.audios.map((_, at) => `Audio${at + 1}`) : []),
    ...(c.elementTags ? c.draft.elements.map((_, at) => `Element${at + 1}`) : []),
    // Kling 4.0's voices, in their pick order (G2-kling-v4-voices).
    ...(c.voiceTags ? c.draft.voices.map((_, at) => `Voice${at + 1}`) : []),
  ];
  // Live's "Create Element" over the composer.
  const cover =
    editing === null ? undefined : (
      <ElementsEditor
        // A new element or another one starts the editor afresh.
        key={String(editing)}
        c={c}
        editing={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
      />
    );
  // A tool with a form of its own replaces the composer, as live's form switch does (#211 to #214,
  // #238): drawn with the page's own toolbar above it, or null on a tool without one.
  const formType = c.action?.form;
  const toolForm = (around: { above: ReactNode; overlay: ReactNode; inline?: boolean }) => {
    if (formType === undefined) return null;
    const Form = FORMS[formType];
    return (
      <Form
        key={formType}
        catalog={c.catalog}
        above={around.above}
        overlay={around.overlay}
        onStarted={props.onStarted}
        actor={arrival}
        clone={c.formClone}
        inline={around.inline}
      />
    );
  };
  return {
    c,
    header,
    above,
    overlay,
    toolForm,
    cover,
    promptTags,
    chooseAction,
    advancedOpen,
    toggleAdvanced: () => {
      setVoicesOpen(false);
      setAdvancedOpen((open) => !open);
    },
    voicesPanel,
  };
}
